"use client"

import { useEffect, useState, useRef } from "react"
import { Button } from "@/components/ui/button"
import { ChevronRight, ChevronLeft, Moon, Zap, Heart, Sparkles, HelpCircle } from "lucide-react"
import DirectImage from "@/components/ui/direct-image"
import Link from "next/link"
import { getProducts, type ProductDisplay } from '@/lib/api/dutchie'
import { formatCategoryName } from "@/lib/format-text"
import ProductCard from "@/components/shop/product-card"
import BannerSlider from "@/components/shop/BannerSlider"
import ShopSpecialsSection from "@/components/shop/shop-specials-section"
import { useListingScrollRestore } from "@/lib/hooks/use-listing-scroll"

// Vibe/Occasion tiles
const vibeTiles = [
  { id: 'relax', title: 'Relax', icon: Moon, color: 'from-indigo-500 to-purple-600', link: '/shop/collection/relax' },
  { id: 'energy', title: 'Energy', icon: Zap, color: 'from-amber-500 to-orange-500', link: '/shop/collection/energy' },
  { id: 'relief', title: 'Relief', icon: Heart, color: 'from-rose-500 to-pink-500', link: '/shop/collection/pain' },
  { id: 'social', title: 'Social', icon: Sparkles, color: 'from-emerald-500 to-teal-500', link: '/shop/collection/social' },
  { id: 'new', title: 'New?', icon: HelpCircle, color: 'from-sky-500 to-blue-500', link: '/shop/collection/beginners' },
]

interface CategoryData {
  name: string;
  slug: string;
  count: number;
  image: string;
  products: ProductDisplay[];
}

// Product Swiper Component
function ProductSwiper({ title, products, categorySlug }: { title: string; products: ProductDisplay[]; categorySlug: string }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 320
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }
  
  if (products.length === 0) return null
  
  return (
    <div className="relative group/swiper">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 bg-primary_green rounded-full"></div>
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <span className="text-sm text-gray-400 hidden sm:inline">({products.length})</span>
        </div>
        <Link 
          href={`/shop/category/${categorySlug}`} 
          className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-primary_green transition-colors group/link"
        >
          View all 
          <ChevronRight className="h-4 w-4 group-hover/link:translate-x-0.5 transition-transform" />
        </Link>
      </div>
      
      {/* Scroll buttons - appear on hover */}
      <button 
        onClick={() => scroll('left')}
        className="absolute left-0 top-1/2 mt-6 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 shadow-xl border border-gray-100 rounded-full p-3 -ml-4 hidden md:flex opacity-0 group-hover/swiper:opacity-100 transition-all duration-200 hover:scale-110"
      >
        <ChevronLeft className="h-5 w-5 text-gray-700" />
      </button>
      <button 
        onClick={() => scroll('right')}
        className="absolute right-0 top-1/2 mt-6 -translate-y-1/2 z-10 bg-white hover:bg-gray-50 shadow-xl border border-gray-100 rounded-full p-3 -mr-4 hidden md:flex opacity-0 group-hover/swiper:opacity-100 transition-all duration-200 hover:scale-110"
      >
        <ChevronRight className="h-5 w-5 text-gray-700" />
      </button>
      
      {/* Products scroll container */}
      <div 
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.slice(0, 12).map((product) => (
          <div key={`${product.id}-${product.variantId}`} className="flex-shrink-0 w-[165px] sm:w-[185px] snap-start">
            <ProductCard product={product} />
          </div>
        ))}
        {/* View More Card */}
        <Link 
          href={`/shop/category/${categorySlug}`}
          className="flex-shrink-0 w-[165px] sm:w-[185px] snap-start"
        >
          <div className="h-full min-h-[280px] bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-200 hover:border-primary_green hover:from-green-50 hover:to-emerald-50 transition-all duration-300 flex flex-col items-center justify-center gap-3 group/more">
            <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center group-hover/more:scale-110 group-hover/more:shadow-md transition-all">
              <ChevronRight className="h-6 w-6 text-gray-400 group-hover/more:text-primary_green transition-colors" />
            </div>
            <span className="text-sm font-medium text-gray-500 group-hover/more:text-primary_green transition-colors">View All {title}</span>
          </div>
        </Link>
      </div>
    </div>
  )
}

export default function ShopPage() {
  const [categories, setCategories] = useState<CategoryData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  useListingScrollRestore(!loading)

  // Main products fetch
  useEffect(() => {
    async function fetchProducts() {
      try {
        setLoading(true)
        setError(null)
        const allProducts = await getProducts()

        if (allProducts.length === 0) {
          setError("No products found. You may need to run the update-cache script.")
        }
        
        // Get unique categories with their products
        const uniqueCategories: Record<string, CategoryData> = {}
        
        allProducts.forEach(product => {
          if (product.category && !uniqueCategories[product.category]) {
            uniqueCategories[product.category] = {
              name: formatCategoryName(product.category),
              slug: product.category,
              count: 0,
              image: product.image || "/placeholder.png",
              products: []
            }
          }
          if (product.category) {
            uniqueCategories[product.category].count++
            uniqueCategories[product.category].products.push(product)
            // Only update image if the current one is the placeholder
            if (uniqueCategories[product.category].image === "/placeholder.png" && product.image) {
              uniqueCategories[product.category].image = product.image
            }
          }
        })
        
        setCategories(Object.values(uniqueCategories))
      } catch (err) {
        console.error("Error fetching products:", err)
        setError("Failed to load products. Please try again later.")
        setCategories([])
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, []);

  // Banner slider state
  const [bannerImages, setBannerImages] = useState<Array<{desktop: string, mobile: string, alt: string, link?: string}>>([]);
  const [showBanner, setShowBanner] = useState(false);
  
  useEffect(() => {
    let didCancel = false;
    async function fetchBanners() {
      try {
        const res = await fetch(`/api/shop/banner?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) throw new Error('No banner');
        const data = await res.json();
        
        if (!didCancel) {
          // Check if using new banner pairs system
          if (data.bannerPairs && data.activeBanners && Array.isArray(data.activeBanners)) {
            const images = data.activeBanners
              .map((bannerId: string) => {
                const pair = data.bannerPairs[bannerId];
                if (!pair) return null;
                
                return {
                  desktop: pair.desktopFilename ? `/api/banner-image/desktop/${pair.desktopFilename}` : '',
                  mobile: pair.mobileFilename ? `/api/banner-image/mobile/${pair.mobileFilename}` : '',
                  alt: pair.name || 'Shop Banner',
                  link: pair.link
                };
              })
              .filter((img: any) => img && (img.desktop || img.mobile));
            
            setBannerImages(images);
            setShowBanner(data.enabled !== false && images.length > 0);
          } else if (data.desktop || data.mobile) {
            // Legacy single banner support
            if (data.desktop || data.mobile) {
              setBannerImages([{
                desktop: data.desktop || '',
                mobile: data.mobile || '',
                alt: 'Shop Banner'
              }]);
              setShowBanner(data.enabled !== false);
            }
          } else {
            setShowBanner(false);
          }
        }
      } catch {
        if (!didCancel) {
          setShowBanner(false);
        }
      }
    }
    fetchBanners();
    return () => { didCancel = true; };
  }, []);

  // Helper to get products by category
  const getProductsByCategory = (categorySlug: string) => {
    const cat = categories.find(c => {
      const slug = c.slug.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
      return slug === categorySlug || c.slug === categorySlug;
    });
    return cat?.products || [];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Promotional Banner */}
      {showBanner && bannerImages.length > 0 && (
        <div className="container mx-auto px-4 pt-4">
          <div className="rounded-xl overflow-hidden">
            <BannerSlider images={bannerImages} autoplaySpeed={5000} />
          </div>
        </div>
      )}

      {/* Quick Navigation Bar */}
      <div className="container mx-auto px-4 pt-4">
        <div className="flex items-center gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
          <Link 
            href="/shop/category" 
            className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-primary_green text-white font-semibold text-sm rounded-full hover:bg-primary_green/90 transition-colors shadow-sm"
          >
            View All Categories
            <ChevronRight className="h-4 w-4" />
          </Link>
          <Link 
            href="/specials" 
            className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-sm rounded-full hover:opacity-90 transition-opacity shadow-sm"
          >
            Specials & Deals
          </Link>
          <Link 
            href="/shop/brand" 
            className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 font-medium text-sm rounded-full hover:border-primary_green hover:text-primary_green transition-colors"
          >
            Shop by Brand
          </Link>
          <Link 
            href="/shop/all" 
            className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 font-medium text-sm rounded-full hover:border-primary_green hover:text-primary_green transition-colors"
          >
            All Products
          </Link>
        </div>
      </div>

      {/* Main shop content */}
      <div className="container mx-auto px-4 pt-4 pb-16">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-pulse flex flex-col items-center">
              <div className="h-14 w-14 bg-gradient-to-br from-primary_green/20 to-emerald-200/40 rounded-2xl mb-4"></div>
              <div className="h-4 w-36 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-gradient-to-r from-red-50 to-rose-50 text-red-800 p-6 rounded-2xl shadow-sm border border-red-100 max-w-2xl mx-auto text-center">
            <p className="font-medium">{error}</p>
            <Button variant="outline" className="mt-4 bg-white hover:bg-red-50" onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        ) : (
          <div className="space-y-12">
            
            {/* ===== SECTION 1: Categories Grid ===== */}
            <section className="bg-white -mx-4 px-4 py-8 md:mx-0 md:rounded-2xl md:px-8 md:shadow-sm md:border md:border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-6 bg-primary_green rounded-full"></div>
                  <h2 className="text-xl font-bold text-gray-900">Shop by Category</h2>
                </div>
                <Link href="/shop/category" className="text-sm font-medium text-gray-500 hover:text-primary_green flex items-center gap-1 transition-colors">
                  View all <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-3 md:gap-5">
                {categories.slice(0, 8).map((category) => {
                  const formattedSlug = category.slug.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
                  const categoryMapping: Record<string, string> = {
                    'vaporizers': 'vaporizers', 'pre-rolls': 'pre-rolls', 'prerolls': 'pre-rolls',
                    'edibles': 'edibles', 'orals': 'edibles', 'tinctures': 'tinctures',
                    'flower': 'flower', 'concentrates': 'concentrates', 'accessories': 'accessories',
                    'topicals': 'topicals', 'apparel': 'apparel'
                  };
                  const displayNameMapping: Record<string, string> = {
                    'vaporizers': 'Vapes', 'pre-rolls': 'Pre-Rolls', 'edibles': 'Edibles',
                    'tinctures': 'Tinctures', 'flower': 'Flower', 'concentrates': 'Concentrates',
                    'accessories': 'Accessories', 'topicals': 'Topicals', 'apparel': 'Apparel'
                  };
                  const urlPath = categoryMapping[formattedSlug] || formattedSlug;
                  const displayName = displayNameMapping[urlPath] || category.name;
                  
                  return (
                    <Link 
                      key={category.slug} 
                      href={`/shop/category/${urlPath}`} 
                      className="group text-center"
                    >
                      <div className="aspect-square relative bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 overflow-hidden group-hover:border-primary_green/30 group-hover:shadow-xl group-hover:shadow-primary_green/5 transition-all duration-300 group-hover:-translate-y-1">
                        <DirectImage
                          src={category.image || "/placeholder.png"}
                          alt={category.name}
                          fill
                          className="p-3"
                          imgClassName="object-contain group-hover:scale-105 transition-transform duration-300"
                          fallbackSrc="/placeholder-image.png"
                        />
                      </div>
                      <p className="mt-2.5 text-xs sm:text-sm font-semibold text-gray-700 group-hover:text-primary_green transition-colors">{displayName}</p>
                    </Link>
                  );
                })}
              </div>
            </section>

            {/* ===== SECTION 2: What's the Vibe? ===== */}
            <section>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-1 h-6 bg-primary_green rounded-full"></div>
                <h2 className="text-xl font-bold text-gray-900">What's the vibe?</h2>
                <span className="text-sm text-gray-400 hidden sm:inline">Shop by occasion</span>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-5" style={{ scrollbarWidth: 'none' }}>
                {vibeTiles.map((vibe) => {
                  const Icon = vibe.icon;
                  return (
                    <Link
                      key={vibe.id}
                      href={vibe.link}
                      className="flex-shrink-0 w-[100px] md:w-auto group"
                    >
                      <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${vibe.color} p-4 md:p-5 text-white transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-lg`}>
                        <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -mr-6 -mt-6"></div>
                        <Icon className="h-6 w-6 mb-2 relative z-10" />
                        <p className="font-semibold text-sm relative z-10">{vibe.title}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>

            {/* ===== SECTION 3: Specials/Deals ===== */}
            <section>
              <ShopSpecialsSection />
            </section>

            {/* ===== SECTION 4: Top Category Swipers ===== */}
            <div className="space-y-12">
              {/* Flower */}
              <ProductSwiper 
                title="Flower" 
                products={getProductsByCategory('flower')} 
                categorySlug="flower" 
              />
              
              {/* Vapes */}
              <ProductSwiper 
                title="Vapes" 
                products={getProductsByCategory('vaporizers')} 
                categorySlug="vaporizers" 
              />
              
              {/* Edibles */}
              <ProductSwiper 
                title="Edibles" 
                products={getProductsByCategory('edibles')} 
                categorySlug="edibles" 
              />
            </div>

            {/* ===== SECTION 5: Trust Bar ===== */}
            <section className="bg-gradient-to-r from-gray-50 to-gray-100 -mx-4 px-4 py-6 md:mx-0 md:rounded-xl">
              <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <div className="w-8 h-8 rounded-full bg-primary_green/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary_green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <span className="font-medium">Licensed Dispensary</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <div className="w-8 h-8 rounded-full bg-primary_green/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary_green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <span className="font-medium">Lab Tested Products</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <div className="w-8 h-8 rounded-full bg-primary_green/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary_green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="font-medium">Fast Pickup & Delivery</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <div className="w-8 h-8 rounded-full bg-primary_green/10 flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary_green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <span className="font-medium">Buffalo, NY Local</span>
                </div>
              </div>
            </section>

            {/* ===== SECTION 6: More Category Swipers ===== */}
            <div className="space-y-12">
              {/* Pre-Rolls */}
              <ProductSwiper 
                title="Pre-Rolls" 
                products={getProductsByCategory('pre-rolls')} 
                categorySlug="pre-rolls" 
              />
              
              {/* Concentrates */}
              <ProductSwiper 
                title="Concentrates" 
                products={getProductsByCategory('concentrates')} 
                categorySlug="concentrates" 
              />
            </div>

            {/* ===== SECTION 7: View All CTA ===== */}
            <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 -mx-4 px-6 py-12 md:mx-0 md:rounded-2xl text-center">
              {/* Decorative elements */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-primary_green/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-2">Can't find what you're looking for?</h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">Browse our full catalog or explore all categories to discover more products.</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button asChild size="lg" className="bg-primary_green hover:bg-green-600 text-white shadow-lg shadow-primary_green/25 hover:shadow-xl hover:shadow-primary_green/30 transition-all">
                    <Link href="/shop/category">View All Categories</Link>
                  </Button>
                  <Button asChild size="lg" className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg">
                    <Link href="/shop/all">Browse All Products</Link>
                  </Button>
                </div>
              </div>
            </section>
            
          </div>
        )}
      </div>
    </div>
  );
}
